/**
 * (c) copyright 2012-2022 mgm technology partners GmbH.
 * This software, the underlying source code and other artifacts are protected by copyright.
 * All rights, in particular the right to use, reproduce, publish and edit are reserved.
 * A simple right of use (license) can be acquired for use, duplication, publication, editing etc..
 * Requests for this can be made at A12-license@mgm-tp.com or other official channels of the copyright holder.
 */
/**
 * @packageDocumentation
 * @module models
 */

import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";

import { FormModelUtils } from "./form-model-utils";

/**
 * @internal
 *
 * Visitor for Form Models for usage with a {@link ModelWalker}.
 * There is one visit function for each Form Model element type.
 *
 * The enter and leave functions provide access to the path of the visited
 * elements. There is no common base type, therefore object is used.
 */
export interface ModelVisitor {
	visitContent?(content: FormModel.Content): VisitProcess;
	visitScreen?(screen: FormModel.Screen): VisitProcess;
	visitSection?(section: FormModel.Section): VisitProcess;
	visitMultiColumnSection?(section: FormModel.MultiColumnSection): VisitProcess;
	visitDetachedRepeat?(repeat: FormModel.DetachedRepeat): VisitProcess;
	visitControlGrid?(grid: FormModel.ControlGrid): VisitProcess;
	visitRow?(row: FormModel.Row): VisitProcess;
	visitControl?(control: FormModel.Control): VisitProcess;
	visitTextCell?(textCell: FormModel.TextCell): VisitProcess;
	visitExpressionCell?(exprCell: FormModel.ExpressionCell): VisitProcess;
	visitInlineRepeat?(repeat: FormModel.InlineRepeat): VisitProcess;
	visitEmbeddedRepeat?(repeat: FormModel.EmbeddedRepeat): VisitProcess;
	visitRepeatOverviewColumn?(repeatColumn: FormModel.RepeatOverviewColumn): VisitProcess;
	visitRowAction?(action: FormModel.RowAction): VisitProcess;
	visitButtonPanel?(panel: FormModel.ButtonPanel): VisitProcess;
	visitButton?(button: FormModel.ButtonType): VisitProcess;
	visitSubHeadingBox?(header: FormModel.HeaderFooterType): VisitProcess;
	visitFooterBox?(footer: FormModel.HeaderFooterType): VisitProcess;
	visitFieldConfigurationEntry?(fce: FormModel.FieldConfigurationEntry): VisitProcess;
	enter?(elementStack: object[]): void;
	leave?(): void;
}

/** @internal */
export enum VisitProcess {
	ContinueTraversal,
	ContinueButDoNotGoDeeper,
	Stop
}

/**
 * @internal
 *
 * A walker for the Form Model. You need to pass a `Visitor` and call
 * one of the accept methods to start the iteration.
 *
 * The walker will them iterate the model, starting with the given element,
 * and call the respective visitor functions. Note that the visitor function
 * will not be called for the passed element itself.
 */
export class ModelWalker {
	private path: object[] = [];
	constructor(private visitor: ModelVisitor) {}

	acceptGeneric(formModelElement: object): void {
		if (FormModel.isInstance(formModelElement)) {
			this.acceptModel(formModelElement);
		} else if (FormModel.Content.isInstance(formModelElement)) {
			this.acceptContent(formModelElement);
		} else if (FormModelUtils.isHeaderFooterTypeInstance(formModelElement)) {
			this.acceptHeaderFooter(formModelElement);
		} else if (FormModel.Screen.isInstance(formModelElement)) {
			this.acceptScreen(formModelElement);
		} else if (FormModel.ButtonType.isInstance(formModelElement)) {
			this.acceptButton(formModelElement);
		} else if (FormModel.ButtonPanel.isInstance(formModelElement)) {
			this.acceptButtonPanel(formModelElement);
		} else if (FormModel.Section.isInstance(formModelElement)) {
			this.acceptSection(formModelElement);
		} else if (FormModel.MultiColumnSection.isInstance(formModelElement)) {
			this.acceptMultiColumnSection(formModelElement);
		} else if (FormModel.ControlGrid.isInstance(formModelElement)) {
			this.acceptControlGrid(formModelElement);
		} else if (FormModel.Row.isInstance(formModelElement)) {
			this.acceptRow(formModelElement);
		} else if (FormModel.InlineRepeat.isInstance(formModelElement)) {
			this.acceptInlineRepeat(formModelElement);
		} else if (FormModel.DetachedRepeat.isInstance(formModelElement)) {
			this.acceptDetachedRepeat(formModelElement);
		} else if (FormModel.EmbeddedRepeat.isInstance(formModelElement)) {
			this.acceptEmbeddedRepeat(formModelElement);
		} else if (FormModel.RepeatOverviewColumn.isInstance(formModelElement)) {
			this.acceptRepeatOverviewColumn(formModelElement);
		} else if (
			FormModel.ExpressionCell.isInstance(formModelElement) ||
			FormModel.TextCell.isInstance(formModelElement) ||
			FormModel.Control.isInstance(formModelElement)
		) {
			this.acceptCell(formModelElement);
		}
	}

	acceptModel(model: FormModel): void {
		this.acceptContent(model.content);
	}

	acceptContent(content: FormModel.Content): void {
		let process = VisitProcess.ContinueTraversal;

		if (this.visitor.visitContent) {
			try {
				process = this.visitor.visitContent(content);
				if (VisitProcess.Stop === process) {
					return;
				}
				// eslint-disable-next-line no-empty
			} finally {
			}
		}

		for (const screen of content.screens) {
			if (!this.acceptScreen(screen)) {
				return;
			}
		}

		if (content.fieldConfiguration.field) {
			for (const fce of content.fieldConfiguration.field) {
				if (!this.acceptFieldConfigurationEntry(fce)) {
					return;
				}
			}
		}

		if (!this.acceptHeaderFooter(content.subHeaderBox)) {
			return;
		}

		this.acceptHeaderFooter(content.footerBox);
	}

	acceptHeaderFooter(headerFooter: FormModel.HeaderFooterType): boolean {
		this.enter(headerFooter);
		try {
			const processButtonsList = (list?: FormModel.ButtonList) => {
				if (list) {
					if (list.button) {
						for (const button of list.button) {
							if (!this.acceptButton(button)) {
								return false;
							}
						}
					}
				}
				return true;
			};
			return processButtonsList(headerFooter.majorButtons) && processButtonsList(headerFooter.minorButtons);
		} finally {
			this.leave();
		}
	}

	acceptFieldConfigurationEntry(fce: FormModel.FieldConfigurationEntry): boolean {
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitFieldConfigurationEntry) {
				process = this.visitor.visitFieldConfigurationEntry(fce);
			}
			return VisitProcess.Stop !== process;
			// eslint-disable-next-line no-empty
		} finally {
		}
	}

	enter(formModelElement: object): void {
		this.path.push(formModelElement);
		if (this.visitor.enter) {
			this.visitor.enter(this.path);
		}
	}

	leave(): void {
		this.path.pop();
		if (this.visitor.leave) {
			this.visitor.leave();
		}
	}

	acceptScreen(screen: FormModel.Screen): boolean {
		this.enter(screen);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitScreen) {
				process = this.visitor.visitScreen(screen);
			}
			if (VisitProcess.ContinueTraversal === process) {
				if (screen.subHeaderBox !== undefined) {
					if (!this.acceptHeaderFooter(screen.subHeaderBox)) {
						process = VisitProcess.Stop;
					}
				}
				if (VisitProcess.ContinueTraversal === process) {
					for (const screenElement of screen.screenElements) {
						if (!this.acceptScreenElement(screenElement)) {
							process = VisitProcess.Stop;
							break;
						}
					}
				}
				if (VisitProcess.ContinueTraversal === process) {
					if (screen.footerBox !== undefined) {
						if (!this.acceptHeaderFooter(screen.footerBox)) {
							process = VisitProcess.Stop;
						}
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptScreenElement(se: FormModel.ScreenElement): boolean {
		switch (se.type) {
			case "ButtonPanel":
				return this.acceptButtonPanel(se);
			case "ControlGrid":
				return this.acceptControlGrid(se);
			case "DetachedRepeat":
				return this.acceptDetachedRepeat(se);
			case "InlineRepeat":
				return this.acceptInlineRepeat(se);
			case "EmbeddedRepeat":
				return this.acceptEmbeddedRepeat(se);
			case "MultiColumnSection":
				return this.acceptMultiColumnSection(se);
			case "Section":
				return this.acceptSection(se);
			default:
				throw new Error();
		}
	}

	acceptButtonPanel(panel: FormModel.ButtonPanel): boolean {
		this.enter(panel);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitButtonPanel) {
				process = this.visitor.visitButtonPanel(panel);
			}
			const buttons = panel.button;
			if (VisitProcess.ContinueTraversal === process && buttons) {
				for (const button of buttons) {
					if (!this.acceptButton(button)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptButton(button: FormModel.ButtonType): boolean {
		this.enter(button);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitButton) {
				process = this.visitor.visitButton(button);
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptControlGrid(grid: FormModel.ControlGrid): boolean {
		this.enter(grid);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitControlGrid) {
				process = this.visitor.visitControlGrid(grid);
			}
			const rows = grid.row;
			if (VisitProcess.ContinueTraversal === process && rows) {
				for (const row of rows) {
					if (!this.acceptRow(row)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptRow(row: FormModel.Row): boolean {
		this.enter(row);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitRow) {
				process = this.visitor.visitRow(row);
			}
			const cells = row.cell;
			if (VisitProcess.ContinueTraversal === process && cells) {
				for (const cell of cells) {
					if (!this.acceptCell(cell)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptCell(cell: FormModel.CellType): boolean {
		this.enter(cell);
		let continueProcess = true;
		try {
			switch (cell.type) {
				case "Control":
					continueProcess =
						this.visitor.visitControl === undefined ||
						this.visitor.visitControl(cell) !== VisitProcess.Stop;
					break;
				case "ExpressionCell":
					continueProcess =
						this.visitor.visitExpressionCell === undefined ||
						this.visitor.visitExpressionCell(cell) !== VisitProcess.Stop;
					break;
				case "TextCell":
					continueProcess =
						this.visitor.visitTextCell === undefined ||
						this.visitor.visitTextCell(cell) !== VisitProcess.Stop;
					break;
				default:
					throw new Error();
			}
			return continueProcess;
		} finally {
			this.leave();
		}
	}

	acceptDetachedRepeat(repeat: FormModel.DetachedRepeat): boolean {
		this.enter(repeat);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitDetachedRepeat) {
				process = this.visitor.visitDetachedRepeat(repeat);
			}
			const columns = repeat.repeatOverviewColumn;
			if (VisitProcess.ContinueTraversal === process && columns) {
				for (const c of columns) {
					if (!this.acceptRepeatOverviewColumn(c)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			const rowActions = repeat.rowActionGroup?.action;
			if (VisitProcess.ContinueTraversal === process && rowActions) {
				for (const r of rowActions) {
					if (!this.acceptRowAction(r)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			if (VisitProcess.ContinueTraversal === process) {
				process = this.acceptScreen(repeat.detailScreen as FormModel.Screen) ? process : VisitProcess.Stop;
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptRepeatOverviewColumn(column: FormModel.RepeatOverviewColumn): boolean {
		this.enter(column);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitRepeatOverviewColumn) {
				process = this.visitor.visitRepeatOverviewColumn(column);
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptRowAction(action: FormModel.RowAction): boolean {
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitRowAction) {
				process = this.visitor.visitRowAction(action);
			}
			return VisitProcess.Stop !== process;
			// eslint-disable-next-line no-empty
		} finally {
		}
	}

	acceptInlineRepeat(repeat: FormModel.InlineRepeat): boolean {
		this.enter(repeat);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitInlineRepeat) {
				process = this.visitor.visitInlineRepeat(repeat);
			}
			const columns = repeat.repeatOverviewColumn;
			if (VisitProcess.ContinueTraversal === process && columns) {
				for (const c of columns) {
					if (!this.acceptRepeatOverviewColumn(c)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			const rowActions = repeat.rowActionGroup?.action;
			if (VisitProcess.ContinueTraversal === process && rowActions) {
				for (const r of rowActions) {
					if (!this.acceptRowAction(r)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptEmbeddedRepeat(repeat: FormModel.EmbeddedRepeat): boolean {
		this.enter(repeat);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitEmbeddedRepeat) {
				process = this.visitor.visitEmbeddedRepeat(repeat);
			}
			const columns = repeat.repeatOverviewColumn;
			if (VisitProcess.ContinueTraversal === process && columns) {
				for (const c of columns) {
					if (!this.acceptRepeatOverviewColumn(c)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			const rowActions = repeat.rowActionGroup?.action;
			if (VisitProcess.ContinueTraversal === process && rowActions) {
				for (const r of rowActions) {
					if (!this.acceptRowAction(r)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			if (VisitProcess.ContinueTraversal === process) {
				process = this.acceptControlGrid(repeat.controlGrid) ? process : VisitProcess.Stop;
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptMultiColumnSection(section: FormModel.MultiColumnSection): boolean {
		this.enter(section);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitMultiColumnSection) {
				process = this.visitor.visitMultiColumnSection(section);
			}
			const elements = section.screenElements;
			if (VisitProcess.ContinueTraversal === process && elements) {
				for (const se of elements) {
					if (!this.acceptScreenElement(se)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}

	acceptSection(section: FormModel.Section): boolean {
		this.enter(section);
		let process = VisitProcess.ContinueTraversal;
		try {
			if (this.visitor.visitSection) {
				process = this.visitor.visitSection(section);
			}
			const elements = section.screenElements;
			if (VisitProcess.ContinueTraversal === process && elements) {
				for (const se of elements) {
					if (!this.acceptScreenElement(se)) {
						process = VisitProcess.Stop;
						break;
					}
				}
			}
			return VisitProcess.Stop !== process;
		} finally {
			this.leave();
		}
	}
}
